import i18n from 'i18next';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../utils/languages';

// The last language the UI was shown in, so the first paint after a reload (and the
// pages before login, which have no profile to read) are already in it.
const STORAGE_KEY = 'rc_ui_language';

function isSupported(code: string): boolean {
  return SUPPORTED_LANGUAGES.some((option) => option.code === code);
}

export function storedLanguage(): string {
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    if (code && isSupported(code)) return code;
  } catch {
    /* storage blocked */
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Switches the whole UI to `code`. i18next fetches the language's file
 * (public/locales/<code>.json) once, then swaps it in — the switch itself is a re-render.
 * An unsupported code falls back to the default rather than showing raw keys.
 */
export function applyLanguage(code: string): void {
  const next = isSupported(code) ? code : DEFAULT_LANGUAGE;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* storage blocked */
  }
  document.documentElement.lang = next;
  if (i18n.resolvedLanguage !== next) void i18n.changeLanguage(next);
}

/**
 * Locale for dates and times shown in the UI: the UI's own language, so a Spanish user
 * reads "20 de octubre de 2026" instead of the browser's English month names. Read at
 * call time, so a language switch is picked up on the next render.
 */
export function uiLocale(): string {
  return i18n.resolvedLanguage || i18n.language || DEFAULT_LANGUAGE;
}
